import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Select, Button, InputNumber, message, Modal, Table, Checkbox, Divider } from "antd";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Toolbar,
  Edit,
  Filter,
  Selection,
} from "@syncfusion/ej2-react-grids";
import withMosadIsolation from "../../utils/withMosadIsolation";
import { fetchTransactions } from "../../redux/slices/transactionsSlice";
import { fetchCustomers } from "../../redux/slices/customersSlice";
import { createTransactionsBulk } from "../../redux/slices/transactionsSlice";
import dayjs from "dayjs";
import { useTranslation } from "../../i18n";
import { printTable } from "../../utils/printTable";

const { Option } = Select;

const Billing = ({ selectedMosad }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.transactions.transactionsList);
  const customers = useSelector((state) => state.customers.customersList);

  const gridRef = useRef(null);

  const { loading, error } = useSelector((state) => state.transactions);

  // states
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [transactionDate, setTransactionDate] = useState(dayjs());
  const [gridData, setGridData] = useState([]);
  const [collectiveSum, setCollectiveSum] = useState(null);
  const [hasEdits, setHasEdits] = useState(false);   // ✅ track unsaved edits

  const [fromCustomer, setFromCustomer] = useState(null);
  const [toCustomer, setToCustomer] = useState(null);
  const [code1, setCode1] = useState(null);
  const [code2, setCode2] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedSum, setSelectedSum] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [useExternNumber, setUseExternNumber] = useState(true);
  const [pickedGridCustomer, setPickedGridCustomer] = useState(null);
  const pendingSelectKeysRef = useRef(new Set());
  const pendingCollectiveSumRef = useRef(null);

  const filterSettings = { type: 'Excel' };

  const getSelectedRecordsWithEdits = () => {
    const grid = gridRef.current;
    const selected = grid?.getSelectedRecords?.() || [];
    if (!selected.length) return [];
    const changed = grid.getBatchChanges?.()?.changedRecords || [];
    if (!changed.length) return selected;
    const changedById = new Map(changed.map((r) => [String(r.cust_sn), r]));
    return selected.map((row) => {
      const patched = changedById.get(String(row.cust_sn));
      return patched ? { ...row, ...patched } : row;
    });
  };

  const updateSelectedSummary = (overrideRow) => {
    let selected = getSelectedRecordsWithEdits();
    if (overrideRow?.cust_sn != null) {
      selected = selected.map((row) =>
        String(row.cust_sn) === String(overrideRow.cust_sn)
          ? { ...row, ...overrideRow }
          : row
      );
    }
    setSelectedCount(selected.length);
    setSelectedSum(
      selected.reduce((acc, row) => acc + (Number(row.charge_sum) || 0), 0)
    );
  };

  const handleSelectionChange = () => {
    setTimeout(updateSelectedSummary, 0);
  };

  const handleChargeSumSaved = (args) => {
    const field = args?.columnName || args?.column?.field;
    if (field !== "charge_sum") return;
    const rowData = args?.rowData;
    const value = args?.value ?? rowData?.charge_sum;
    setTimeout(
      () =>
        updateSelectedSummary(
          rowData ? { ...rowData, charge_sum: value } : undefined
        ),
      0
    );
  };

  const applyChargeSumToGrid = (grid, sum) => {
    if (!grid || sum == null) return;
    const all = Array.isArray(grid.dataSource) ? grid.dataSource : [];
    all.forEach((row) => {
      if (row?.cust_sn == null) return;
      row.charge_sum = sum;
      if (typeof grid.setCellValue === "function") {
        grid.setCellValue(row.cust_sn, "charge_sum", sum);
      }
    });
    if (grid.selectionModule?.persistSelectedData?.length) {
      grid.selectionModule.persistSelectedData =
        grid.selectionModule.persistSelectedData.map((r) => ({
          ...r,
          charge_sum: sum,
        }));
    }
  };

  const applyPendingGridSelection = () => {
    const grid = gridRef.current;
    if (!grid) return;

    const keys = pendingSelectKeysRef.current;
    const pendingSum = pendingCollectiveSumRef.current;
    const shouldRefreshSummary = (keys && keys.size > 0) || pendingSum != null;

    if (pendingSum != null) {
      applyChargeSumToGrid(grid, pendingSum);
    }

    if (keys?.size) {
      const all = Array.isArray(grid.dataSource) ? grid.dataSource : gridData;
      const persistRecords = all.filter((r) => keys.has(String(r.cust_sn)));
      if (persistRecords.length) {
        const view = grid.getCurrentViewRecords?.() || [];
        const indexes = [];
        view.forEach((r, idx) => {
          if (keys.has(String(r.cust_sn))) indexes.push(idx);
        });

        if (indexes.length) {
          if (typeof grid.selectRows === "function") {
            grid.selectRows(indexes);
          } else {
            indexes.forEach((idx) => {
              if (typeof grid.selectionModule?.addRowsToSelection === "function") {
                grid.selectionModule.addRowsToSelection([idx]);
              } else {
                grid.selectRow(idx);
              }
            });
          }
        }

        if (grid.selectionModule) {
          grid.selectionModule.persistSelectedData = persistRecords;
        }
      }
      pendingSelectKeysRef.current = new Set();
    }

    // Re-apply sums after selection restore so old persisted records cannot overwrite cells
    if (pendingSum != null) {
      applyChargeSumToGrid(grid, pendingSum);
      pendingCollectiveSumRef.current = null;
    }

    if (shouldRefreshSummary) {
      setTimeout(updateSelectedSummary, 0);
    }
  };

  const handlePickGridCustomer = (custSn) => {
    setPickedGridCustomer(custSn ?? null);
    if (custSn == null) return;

    const grid = gridRef.current;
    if (!grid) return;

    const selectedKeys = (grid.getSelectedRecords?.() || []).map((r) =>
      String(r.cust_sn)
    );
    pendingSelectKeysRef.current = new Set([...selectedKeys, String(custSn)]);

    const view = grid.getCurrentViewRecords?.() || [];
    const idxInView = view.findIndex((r) => String(r.cust_sn) === String(custSn));
    if (idxInView >= 0) {
      applyPendingGridSelection();
      return;
    }

    const filtered = grid.getFilteredRecords?.();
    const all = Array.isArray(filtered) && filtered.length
      ? filtered
      : (Array.isArray(grid.dataSource) ? grid.dataSource : gridData);
    const globalIndex = all.findIndex((r) => String(r.cust_sn) === String(custSn));
    if (globalIndex < 0) {
      pendingSelectKeysRef.current = new Set();
      return;
    }

    const pageSize = grid.pageSettings?.pageSize || 8;
    const pageNumber = Math.floor(globalIndex / pageSize) + 1;
    if (grid.pageSettings?.currentPage !== pageNumber) {
      grid.goToPage(pageNumber);
    } else {
      applyPendingGridSelection();
    }
  };

  const customerNameTemplate = (props) => {
    if (!props?.name) return "";
    const number = props.number;
    if (number == null || number === "") return props.name;
    return (
      <a
        href={`/customers?number=${encodeURIComponent(number)}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1677ff", textDecoration: "underline" }}
        onClick={(e) => e.stopPropagation()}
      >
        {props.name}
      </a>
    );
  };

  useEffect(() => {
    if (!transactions) {
      dispatch(fetchTransactions());
    }
    if (!customers) {
      dispatch(fetchCustomers());
    }
  }, [dispatch, transactions, customers]);

  useEffect(() => {
    if (error) {
      const messageText = typeof error === 'string' ? error : (error.message || t('notifications.frontend.common.unknownError'));
      message.error(messageText);
    }
  }, [error]);

  // build readable period string


  function formatDateDdMmYyyy(value) {
    if (value === null || value === undefined || value === '') return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function dateValueAccessor(field, data) {
    return formatDateDdMmYyyy(data?.[field]);
  }

  const applySearch = (list) => {
    if (!list || list.length === 0) {
      message.error(t('notifications.frontend.common.noCustomersAvailable'));
      return;
    }

    let filtered = [...list];

    // 0) Filter by charge day: keep customers whose charge_date day matches the selected date's day
    if (transactionDate) {
      const selectedDay = dayjs(transactionDate).date();
      filtered = filtered.filter((c) => {
        if (!c.charge_date) return false;
        return c.charge_date === selectedDay;
      });
    }

    // 1) Filter by customer number range if provided (within charge-date filtered list)
    if (fromCustomer && toCustomer) {
      const fromIndex = filtered.findIndex((c) => c.number === fromCustomer);
      const toIndex = filtered.findIndex((c) => c.number === toCustomer);

      if (fromIndex !== -1 && toIndex !== -1) {
        const [start, end] =
          fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];
        filtered = filtered.slice(start, end + 1);
      }
    }

    // 2) Filter by code1/code2 if provided
    if (code1) {
      filtered = filtered.filter((c) => String(c.code1) === String(code1));
    }
    if (code2) {
      filtered = filtered.filter((c) => String(c.code2) === String(code2));
    }

    // 3) Remove customers with expired payments
    filtered = filtered.filter((c) => {
      if (!c.expired_date) return true;      // if no expire date → keep
      return dayjs(c.expired_date).isAfter(dayjs(), "day");
    });

    // 4) Default filter: only customers with open/unlimited payments
    filtered = filtered.filter(
      (c) =>
        c.payments_number === -1 ||
        (c.paid_payments !== undefined &&
          c.payments_number !== undefined &&
          c.paid_payments < c.payments_number)
    );

    // 4) Map into grid format
    const result = filtered.map((c) => {
      return {
        cust_sn: c.sn,   // ✅ use the new field
        number: c.number,
        id: c.id,                  // keep if you want to display
        name: c.name,
        cust_extern: Number(c.cust_extern) || 0,
        charge_sum: Number(c.charge_sum) || 0,
        charge_date: c.charge_date,
        payment:
          c.payments_number === -1
            ? "-1"
            : c.paid_payments > 0
              ? `${c.paid_payments || 0}/${c.payments_number || 0}`
              : "",
        expired_date: c.expired_date,
        notes: c.notes,
      };
    });

    gridRef.current?.clearSelection?.();
    pendingSelectKeysRef.current = new Set();
    pendingCollectiveSumRef.current = null;
    setSelectedCount(0);
    setSelectedSum(0);
    setPickedGridCustomer(null);
    setGridData(result);
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const response = await dispatch(fetchCustomers()).unwrap();
      applySearch(response?.data || []);
    } catch (err) {
      applySearch(customers);
    } finally {
      setSearchLoading(false);
    }
  };

  function base64ToBlob(base64, mimeType) {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  }

  function formatSum(value) {
    return Number(value || 0).toLocaleString("he-IL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function csvEscape(value) {
    const str = String(value ?? "");
    return /[",\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
  }

  function timestampSuffix() {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const HH = String(now.getHours()).padStart(2, "0");
    const MM = String(now.getMinutes()).padStart(2, "0");
    return `${yy}${mm}${dd}${HH}${MM}`;
  }

  function downloadResultsCsv(created, skipped) {
    const header = ["סטטוס", "מספר לקוח", "שם לקוח", "סכום", "אסמכתא", "סיבה"];
    const rows = [
      header,
      ...created.map((r) => ["נוצר", r.customer_number, r.customer_name, formatSum(r.charge_sum), r.asc_num, ""]),
      ...skipped.map((r) => ["דולג", r.customer_number, r.customer_name, formatSum(r.charge_sum), "", r.reason]),
    ];
    const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\r\n");
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `billing-results-${timestampSuffix()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  function printExportedRows(created) {
    if (!created.length) return;
    const totalSum = created.reduce(
      (acc, row) => acc + (Number(row.charge_sum) || 0),
      0
    );
    const ok = printTable({
      title: `${t("ui.billing.title")} - ${formatDateDdMmYyyy(transactionDate)}`,
      summary: t("ui.billing.exportedSummary", {
        count: created.length,
        sum: formatSum(totalSum),
      }),
      columns: [
        { header: t("ui.billing.resultsCustomerNumber"), format: (r) => r.customer_number ?? "" },
        { header: t("ui.customers.name"), format: (r) => r.customer_name ?? "" },
        { header: t("ui.customers.id"), format: (r) => r.customer_id ?? "" },
        { header: t("ui.customers.chargeSum"), format: (r) => formatSum(r.charge_sum) },
        { header: t("ui.billing.resultsAscNum"), format: (r) => r.asc_num ?? "" },
      ],
      rows: created,
    });
    if (!ok) message.error(t("ui.billing.printPopupBlocked"));
  }

  function showResultsModal(created, skipped) {
    const createdColumns = [
      { title: t('ui.billing.resultsCustomerNumber'), dataIndex: 'customer_number', width: 100 },
      { title: t('ui.customers.name'), dataIndex: 'customer_name' },
      { title: t('ui.customers.chargeSum'), dataIndex: 'charge_sum', width: 100, render: formatSum },
      { title: t('ui.billing.resultsAscNum'), dataIndex: 'asc_num', width: 100 },
    ];
    const skippedColumns = [
      { title: t('ui.billing.resultsCustomerNumber'), dataIndex: 'customer_number', width: 100 },
      { title: t('ui.customers.name'), dataIndex: 'customer_name' },
      { title: t('ui.customers.chargeSum'), dataIndex: 'charge_sum', width: 100, render: formatSum },
      { title: t('ui.billing.resultsReason'), dataIndex: 'reason' },
    ];

    Modal.info({
      title: t('ui.billing.resultsTitle'),
      width: 760,
      content: (
        <div>
          <p style={{ color: "#389e0d", fontWeight: 600 }}>
            {t('ui.billing.resultsSuccessCount', { count: created.length })}
          </p>
          {created.length > 0 && (
            <Table
              size="small"
              pagination={false}
              scroll={{ y: 180 }}
              columns={createdColumns}
              dataSource={created.map((r, i) => ({ ...r, key: `c-${i}` }))}
            />
          )}
          {skipped.length > 0 && (
            <>
              <p style={{ color: "#cf1322", fontWeight: 600, marginTop: 12 }}>
                {t('ui.billing.resultsSkippedCount', { count: skipped.length })}
              </p>
              <Table
                size="small"
                pagination={false}
                scroll={{ y: 180 }}
                columns={skippedColumns}
                dataSource={skipped.map((r, i) => ({ ...r, key: `s-${i}` }))}
              />
            </>
          )}
          <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={() => downloadResultsCsv(created, skipped)}>
              {t('ui.billing.downloadResultsCsv')}
            </Button>
            {created.length > 0 && (
              <Button onClick={() => printExportedRows(created)}>
                {t('ui.billing.printExported')}
              </Button>
            )}
          </div>
        </div>
      ),
    });
  }

  function formatPeriodYYMM(fromDate, toDate) {
    const formatYYMM = (d) => d ? dayjs(d).format("YYMM") : "";
    const from = formatYYMM(fromDate);
    const to = formatYYMM(toDate);
    return from + to; // e.g. "25092510"
  }


  // Customers matching the selected transaction date's charge day (same filter as search)
  const chargeDateFilteredCustomers = useMemo(() => {
    if (!customers?.length) return [];
    if (!transactionDate) return [];
    const selectedDay = dayjs(transactionDate).date();
    return customers.filter((c) => c.charge_date && c.charge_date === selectedDay);
  }, [customers, transactionDate]);

  // Clear from/to if they fall outside the charge-date filter when the date changes
  useEffect(() => {
    const numbers = new Set(chargeDateFilteredCustomers.map((c) => c.number));
    if (fromCustomer != null && !numbers.has(fromCustomer)) {
      setFromCustomer(null);
    }
    if (toCustomer != null && !numbers.has(toCustomer)) {
      setToCustomer(null);
    }
  }, [chargeDateFilteredCustomers, fromCustomer, toCustomer]);

  const filterCustomerOption = (input, option) => {
    const label = String(option?.label ?? "").toLowerCase();
    return label.includes(String(input).toLowerCase());
  };

  const customerSelectOptions = useMemo(
    () =>
      chargeDateFilteredCustomers.map((c) => ({
        value: c.number,
        label: `${c.number} - ${c.name}`,
      })),
    [chargeDateFilteredCustomers]
  );

  const gridNameOptions = useMemo(
    () =>
      gridData
        .filter((r) => r?.name)
        .map((r) => ({
          value: String(r.cust_sn),
          label: r.number != null && r.number !== ""
            ? `${r.name} (${r.number})`
            : r.name,
        })),
    [gridData]
  );

  useEffect(() => {
    if (pickedGridCustomer == null) return;
    const exists = gridData.some(
      (r) => String(r.cust_sn) === String(pickedGridCustomer)
    );
    if (!exists) setPickedGridCustomer(null);
  }, [gridData, pickedGridCustomer]);

  // Unique code lists for dropdowns
  const code1Options = useMemo(() => {
    const values = new Set();
    customers?.forEach((c) => {
      const v = c?.code1;
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        values.add(String(v));
      }
    });
    return Array.from(values);
  }, [customers]);

  const code2Options = useMemo(() => {
    const values = new Set();
    customers?.forEach((c) => {
      const v = c?.code2;
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        values.add(String(v));
      }
    });
    return Array.from(values);
  }, [customers]);

  // apply the same sum to all customers in grid
  const applyCollectiveSum = () => {
    if (!collectiveSum || collectiveSum <= 0) {
      message.error(t('notifications.frontend.common.enterValidCollectiveSum'));
      return;
    }
    if (!gridData.length) {
      message.error(t('notifications.frontend.common.noCustomersInGrid'));
      return;
    }

    const grid = gridRef.current;
    const newSum = Number(collectiveSum);
    const selectedIds = new Set(
      (grid?.getSelectedRecords?.() || []).map((r) => String(r.cust_sn))
    );
    const updated = gridData.map((c) => ({
      ...c,
      charge_sum: newSum,
    }));

    if (grid?.selectionModule?.persistSelectedData?.length) {
      grid.selectionModule.persistSelectedData =
        grid.selectionModule.persistSelectedData.map((r) => ({
          ...r,
          charge_sum: newSum,
        }));
    }

    try {
      grid?.endEdit?.();
    } catch (e) {
      // ignore if no cell is in edit mode
    }

    pendingSelectKeysRef.current = selectedIds;
    pendingCollectiveSumRef.current = newSum;

    if (grid) {
      grid.dataSource = updated;
      if (typeof grid.refresh === "function") {
        grid.refresh();
      }
      applyChargeSumToGrid(grid, newSum);
    }

    setGridData(updated);
    message.success(`Applied ${collectiveSum} to ${gridData.length} customers`);
  };

  // --- Save only changed rows, merge into state, persist, clear batch buffer ---
  const saveChanges = async () => {
    const grid = gridRef.current;
    if (!grid) return;

    // ✅ Check period first
    if (!fromDate || !toDate) {
      message.error("יש לבחור תקופה (מתאריך ועד תאריך) לפני שמירה");
      return;
    }
    // 1) commit the currently edited cell (if any)
    grid.endEdit?.();

    // 2) read batched delta (added/changed/deleted)
    const batch = grid.getBatchChanges?.();
    const changed = batch?.changedRecords || [];
    const added = batch?.addedRecords || [];
    const deleted = batch?.deletedRecords || [];

    // 3) merge changed into local state (by cust_sn — the real unique row id;
    // `id` is the customer's teudat-zehut, which can be blank or duplicated)
    const byId = new Map(gridData.map((r) => [String(r.cust_sn), r]));
    if (changed.length) {
      changed.forEach((row) => {
        const key = String(row.cust_sn);
        const prev = byId.get(key) || {};
        byId.set(key, { ...prev, ...row });
      });
    }

    // if you ever support adds/deletes locally, merge those as well
    if (added.length) {
      added.forEach((row) => {
        const key = String(row.cust_sn);
        byId.set(key, row);
      });
    }
    if (deleted.length) {
      deleted.forEach((row) => {
        const key = String(row.cust_sn);
        byId.delete(key);
      });
    }
    const merged = Array.from(byId.values());
    setGridData(merged);

    if (!merged.length) {
      message.info(t('notifications.frontend.common.noRowsToSave'));
      return;
    }

    // Only charge rows the user actually selected, and only when the sum is positive
    const selectedIds = new Set(
      (grid.getSelectedRecords?.() || []).map((r) => String(r.cust_sn))
    );
    const eligible = merged.filter(
      (row) => selectedIds.has(String(row.cust_sn)) && Number(row.charge_sum) > 0
    );

    if (!eligible.length) {
      message.warning(t('ui.billing.selectRowsForSave'));
      return;
    }

    try {

      // formatTransactionRequestData expects:
      // asc_num, customer, mosad, name, charge_sum, period, charge_date, create_date, note, printed, exported:contentReference[oaicite:5]{index=5}
      const payload = eligible.map((row, idx) => ({
        customer: row.cust_sn,                       // ✅ now valid
        customer_number: row.number ?? "",           // for skip/success reporting
        mosad: selectedMosad?.mosad_code,            // or remove if server sets it
        name: row.name ?? "",
        charge_sum: row.charge_sum ?? 0,
        period: formatPeriodYYMM(fromDate, toDate),  // "YYMMYYMM"
        charge_date: dayjs(transactionDate).format("YYYY-MM-DD"),
        create_date: dayjs().format("YYYY-MM-DD"),
        note: row.notes?.substring(0, 255) || "",
        printed: false,
        exported: false
      }));

      // Save (per-row: some may succeed, some may be skipped) & download MASAV file
      const result = await dispatch(createTransactionsBulk({
        transactions: payload,
        use_extern_number: useExternNumber,
      })).unwrap();
      // attach the customer's id (teudat-zehut) from the charged grid rows, matched by customer number
      const idByNumber = new Map(eligible.map((row) => [String(row.number), row.id]));
      const created = (result?.created || []).map((r) => ({
        ...r,
        customer_id: r.customer_id || idByNumber.get(String(r.customer_number)) || "",
      }));
      const skippedRows = result?.skipped || [];

      // trigger download of the MASAV file, if any row was actually created
      if (result?.file) {
        // יצירת תאריך־ושעה בפורמט YYMMDD_HHMM
        const now = new Date();
        const yy = String(now.getFullYear()).slice(-2);
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const HH = String(now.getHours()).padStart(2, "0");
        const MM = String(now.getMinutes()).padStart(2, "0");

        // שם קובץ דינמי מס"ב
        const fileName = `MSV${yy}${mm}${dd}${HH}${MM}.001`;
        const blob = base64ToBlob(result.file, "text/plain");
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }

      // clean up grid
      grid.batchSave?.();

      // refresh list so UI reflects new transactions
      await dispatch(fetchTransactions());
      await dispatch(fetchCustomers());

      // keep rows that weren't charged (unselected, or sum <= 0) so the user can still act on them
      const chargedIds = new Set(eligible.map((row) => String(row.cust_sn)));
      const remaining = merged.filter((row) => !chargedIds.has(String(row.cust_sn)));
      gridRef.current?.clearSelection?.();
      setGridData(remaining);
      setHasEdits(false);
      setSelectedCount(0);
      setSelectedSum(0);

      showResultsModal(created, skippedRows);
      if (created.length > 0) {
        message.success(t('notifications.frontend.common.actionsSavedAndFileDownloaded'));
      } else {
        message.warning(t('ui.billing.allRowsSkipped'));
      }

      // // 6) bulk INSERT (POST /transactions) via Redux thunk
      // await dispatch(createTransactionsBulk(payload)).unwrap(); // calls transactionsAPI.create → POST /transactions:contentReference[oaicite:6]{index=6}:contentReference[oaicite:7]{index=7}

      // // 7) finalize grid batch & refresh state
      // grid.batchSave?.();
      // dispatch(fetchTransactions()); // optional: refresh after insert

      // // 8) clear the grid per your requirement
      // setGridData([]);
      // setHasEdits(false);
      // message.success("Transactions created and grid cleared.");
    } catch (err) {
      console.error("Bulk insert failed:", err);
      message.error(t('notifications.frontend.common.failedCreateTransactions'));
    }
  };
  //   try {
  //     // 4) persist to backend — send only what changed
  //     await api.post("/billing/update", {
  //       changedRecords: changed,
  //       addedRecords: added,
  //       deletedRecords: deleted,
  //     });

  //     // 5) ask the grid to finalize the batch and clear its buffer
  //     grid.batchSave?.();

  //     // 6) refresh Redux so the rest of the app sees the new values
  //     dispatch(fetchCustomers());

  //     setHasEdits(false);
  //     message.success("Billing changes saved.");
  //   } catch (err) {
  //     console.error("Save failed:", err);
  //     message.error("Failed to save billing changes.");
  //   }
  // };

  const cancelChanges = () => {
    const grid = gridRef.current;
    grid?.closeEdit?.(); // discard batch buffer
    setHasEdits(false);
    message.info(t('notifications.frontend.common.changesCanceled'));
  };

  const deleteSelected = () => {
    const grid = gridRef.current;
    if (!grid) return;

    const selected = grid.getSelectedRecords?.() || [];
    if (!selected.length) {
      message.warning(t('notifications.frontend.common.noRowsSelected'));
      return;
    }

    const selectedIds = new Set(selected.map((r) => String(r.cust_sn)));
    setGridData((prev) => prev.filter((r) => !selectedIds.has(String(r.cust_sn))));
    grid.clearSelection?.();
    setSelectedCount(0);
    setSelectedSum(0);
    message.success(t('notifications.frontend.common.rowsDeleted'));
  };

  return (
    <div className='control-pane'>
      <div className='control-section'>
        <h2>{t('ui.billing.title')}</h2>
        {/* === First Row: Period & Transaction Date === */}
        <div style={{ marginBottom: 20, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "row", gap: 10, flexWrap: "wrap" }}>
          <DatePicker
            picker="month"
            placeholder={t('ui.billing.fromPeriod')}
            onChange={(v) => setFromDate(v)}
          />
          <DatePicker
            picker="month"
            placeholder={t('ui.billing.toPeriod')}
            onChange={(v) => setToDate(v)}
          />
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{color: "rgba(0, 0, 0, 0.85)" }}>
              {t('ui.billing.selectDate')}
            </label>
            <DatePicker
            placeholder={t('ui.billing.selectDate')}
              value={transactionDate}
              onChange={(v) => setTransactionDate(v)}
            />
          </div>
        </div>

        {/* === Second Row: From/To Customers + Search === */}
        <div style={{ marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span style={{color: "rgba(0, 0, 0, 0.85)" }}>
            {t('common.filter')}
          </span>
          <Select
            showSearch
            placeholder={t('ui.billing.fromCustomer')}
            style={{ width: 220 }}
            value={fromCustomer}
            onChange={(v) => setFromCustomer(v)}
            allowClear
            options={customerSelectOptions}
            filterOption={filterCustomerOption}
          />

          <Select
            showSearch
            placeholder={t('ui.billing.toCustomer')}
            style={{ width: 220 }}
            value={toCustomer}
            onChange={(v) => setToCustomer(v)}
            allowClear
            options={customerSelectOptions}
            filterOption={filterCustomerOption}
          />

          <Select
            placeholder={t('ui.billing.code1')}
            style={{ width: 220 }}
            value={code1}
            onChange={(v) => setCode1(v)}
            allowClear
          >
            {code1Options.map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Select>

          <Select
            placeholder={t('ui.billing.code2')}
            style={{ width: 220 }}
            value={code2}
            onChange={(v) => setCode2(v)}
            allowClear
          >
            {code2Options.map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Select>

          <Button
            type="primary"
            onClick={handleSearch}
            loading={searchLoading}
            disabled={!transactionDate}
          >
            {t('ui.billing.search')}
          </Button>
        </div>

        {/* === Collective Sum Input + Apply & Save/Cancel Buttons === */}
        <div style={{ marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span>{t('ui.billing.collectiveSum')}: </span>
          <InputNumber
            value={collectiveSum}
            onChange={(v) => setCollectiveSum(v)}
          />
          <Button onClick={applyCollectiveSum} variant="outlined" color="primary">
            {t('ui.billing.applyToAll')}
          </Button>
          <Button onClick={saveChanges} variant="solid" color="primary" disabled={loading}>
            {t('ui.billing.saveAndCharge')}
          </Button>
          <Button danger onClick={deleteSelected} disabled={!gridData.length}>
            {t('ui.billing.deleteSelected')}
          </Button>
          {hasEdits && (
            <Button danger onClick={cancelChanges}>
              {t('ui.billing.cancelChanges')}
            </Button>
          )}
        </div>

        {selectedCount > 0 && (
          <div
            style={{
              marginBottom: 8,
              color: "#1677ff",
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            {t("ui.billing.selectedMovesSummary", {
              count: selectedCount,
              sum: selectedSum.toLocaleString("he-IL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            })}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <Checkbox
            checked={useExternNumber}
            onChange={(e) => setUseExternNumber(e.target.checked)}
          >
            {t('ui.billing.useExternNumberInMasav')}
          </Checkbox>
        </div>

        <Divider></Divider>

        {/* === Find Customer in Grid === */}
        <div style={{ marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span>{t('ui.billing.findCustomerInGrid')}:</span>
          <Select
            showSearch
            allowClear
            placeholder={t('ui.billing.findCustomerInGridPlaceholder')}
            style={{ width: 280 }}
            value={pickedGridCustomer}
            onChange={handlePickGridCustomer}
            options={gridNameOptions}
            filterOption={filterCustomerOption}
            disabled={!gridData.length}
          />
        </div>

        {/* === Syncfusion Grid === */}
        <div style={{ width: "100%", overflowX: "auto", marginTop: 20 }}>
        <GridComponent
          ref={gridRef}
          dataSource={gridData}
          width="100%"
          allowSorting={true}
          allowFiltering={true}
          filterSettings={filterSettings}
          allowPaging={true}
          pageSettings={{ pageSize: 8 }}
          loading={loading}
          enableRtl={true}
          allowKeyboard={true}
          selectionSettings={{ type: 'Multiple', checkboxOnly: true, persistSelection: true }}
          rowSelected={handleSelectionChange}
          rowDeselected={handleSelectionChange}
          checkBoxChange={handleSelectionChange}
          dataBound={() => setTimeout(applyPendingGridSelection, 0)}
          cellSave={handleChargeSumSaved}
          cellSaved={handleChargeSumSaved}
          editSettings={{
            allowEditing: true,
            allowAdding: false,
            allowDeleting: true,
            mode: "Batch", // ✅ batch editing mode
            showConfirmDialog: false,        // ✅ disables "Are you sure?" on Save  
          }}
          // cellEdit={markEdited}
          // batchAdd={markEdited}
          // batchDelete={markEdited}
          // batchSave={markSaved}
          allowTextWrap={true}
          textWrapSettings={{ wrapMode: 'Both' }}

        >
          <ColumnsDirective>
            <ColumnDirective type="checkbox" width="50" />
            {/* cust_sn is the true unique row id (the teudat-zehut "id" field can be blank or shared) */}
            <ColumnDirective field="cust_sn" isPrimaryKey={true} visible={false} />
            <ColumnDirective field="number" headerText={t('ui.billing.resultsCustomerNumber')} width="100" textAlign="Center" allowEditing={false} />
            <ColumnDirective field="cust_extern" headerText={t('ui.customers.externNumber')} width="110" textAlign="Center" type="number" allowEditing={false} />
            <ColumnDirective field="name" headerText={t('ui.customers.name')} width="120" textAlign="Center" allowEditing={false} template={customerNameTemplate} />
            <ColumnDirective field="id" headerText={t('ui.customers.id')} width="120" textAlign="Center" allowEditing={false} />
            <ColumnDirective field="charge_sum" headerText={t('ui.customers.chargeSum')} width="100" textAlign="Center" type="number" editType="numericedit" format="C2" validationRules={{ required: true, min: 0 }}
              edit={{
                params: {
                  decimals: 2,       // ✅ forces editor to keep .00
                  step: 0.01,        // ✅ increments by hundredths
                  format: 'C2',      // ✅ apply same currency format inside editor
                }
              }}
            />
            {/* <ColumnDirective
            field="charge_sum"
            headerText="Charge Sum"
            width="100"
            textAlign="Center"
            editType="numericedit"
            format="C2"   // ✅ show currency with 2 decimals
            validationRules={{ required: true, min: 0 }}
            edit={{
              params: {
                decimals: 2,
                step: 0.01,
                format: 'C2',      // ✅ currency also in edit mode
                currency: 'ILS',   // ✅ shekel
                culture: 'he-IL'   // ✅ proper Hebrew/Israel culture
              }
            }}/> */}
            <ColumnDirective field="expired_date" headerText={t('ui.billing.expireDate')} width="100" textAlign="Center" allowEditing={false} valueAccessor={dateValueAccessor} />
            <ColumnDirective field="payment" headerText={t('ui.billing.payment')} width="120" textAlign="Center" allowEditing={false} />
            <ColumnDirective
              field="notes"
              headerText={t('ui.customers.notes')}
              width="220"
              allowEditing={true}   // ✅ make editable
              editType="stringedit" // ✅ plain text input
            />
            {/* <ColumnDirective field="paid_payments" headerText="Paid Payments" width="100" textAlign="Center" allowEditing={false} /> */}
            {/* <ColumnDirective field="payments_number" headerText="Payments Number" width="100" textAlign="Center" allowEditing={false} /> */}
            {/* <ColumnDirective field="expired_date" headerText="Expired Date" width="150" textAlign="Center" allowEditing={false} valueAccessor={dateValueAccessor}/> */}
            {/* <ColumnDirective field="notes" headerText="Notes" width="200" textAlign="Center" allowEditing={false} /> */}
          </ColumnsDirective>
          <Inject services={[Page, Toolbar, Edit, Filter, Selection]} />
        </GridComponent>
        </div>

      </div>
    </div>
  );
};

export default withMosadIsolation(Billing);